import React, { useState, useEffect } from "react";
import { Table, Button, Input, message, Modal } from "antd";
import { LeftOutlined, BulbOutlined, PlusOutlined } from "@ant-design/icons";
import { Edit, Trash2 } from "lucide-react";
import AssetModal from "../../../../../modal/admin/addAssetModal";
import EditAssetModal from '../../../../../modal/admin/editAssetModal';
import { getRequest, deleteRequest } from "../../../../../service/index";
import { getEmployeeData } from "../../../../../utils/localStorage";
import { formatDate } from "../../../../../utils/dateTime";

const Assets = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const employeeData = getEmployeeData();
  const userRole = employeeData?.role;
  const assetsPermissions = employeeData?.designation?.permissions?.Organization?.Assest || [];

  const canView = userRole === "companyadmin" || assetsPermissions.includes("view");
  const canAdd = userRole === "companyadmin" || assetsPermissions.includes("add");
  const canEdit = userRole === "companyadmin" || assetsPermissions.includes("edit");
  const canDelete = userRole === "companyadmin" || assetsPermissions.includes("delete");

  // fetchAssets must be defined before useEffect if used in dependency array or callbacks
  const fetchAssets = async () => {
    setLoading(true);
    try {
      if (!canView) {
        setAssets([]);
      } else {
        const response = await getRequest("/api/assets");
        // Flatten assetDetails for table display
        const flatAssets = Array.isArray(response)
          ? response.flatMap(asset =>
              (asset.assetDetails || []).map(detail => ({
                _id: asset._id,
                employeeId: asset.employeeId,
                productName: detail.productName,
                serialNumber: detail.serialNumber,
                assignDate: detail.assignDate,
                description: detail.description,
                inventoryId: detail.inventoryId,
              }))
            )
          : [];
        setAssets(flatAssets);
      }
    } catch (error) {
      message.error("Failed to fetch assets");
      setAssets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  // Handler for deleting an asset
  const handleDeleteAsset = (record) => {
    if (!canDelete) {
      message.warning("You don't have permission to delete assets.");
      return;
    }
    Modal.confirm({
      title: 'Are you sure you want to delete this asset?',
      content: `Product: ${record.productName}`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteRequest(`/api/assets?id=${record._id}`);
          message.success('Asset deleted successfully');
          fetchAssets();
        } catch (error) {
          message.error('Failed to delete asset');
        }
      },
    });
  };

  const columns = [
    {
      title: "Holder Name",
      dataIndex: "employeeName",
      key: "employeeName",
      render: (_, record) => {
        if (record.employeeName) return record.employeeName;
        if (record.employeeId && typeof record.employeeId === 'object') {
          return `${record.employeeId.firstName || ''} ${record.employeeId.lastName || ''}`.trim();
        }
        return "-";
      }
    },
    {
      title: "Product Name",
      dataIndex: "productName",
      key: "productName",
    },
    {
      title: "Serial Number",
      dataIndex: "serialNumber",
      key: "serialNumber",
    },
    {
      title: "Assign Date",
      dataIndex: "assignDate",
      key: "assignDate",
      render: (date) => date ? formatDate(date) : "-",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex gap-2">
          <Button
            type="text"
            icon={<Edit className="w-4 h-4 text-blue-600" />}
            disabled={!canEdit}
            onClick={() => {
              if (!canEdit) {
                message.warning("You don't have permission to edit assets.");
                return;
              }
              setSelectedAsset(record);
              setIsEditModalOpen(true);
            }}
          />
          <Button
            type="text"
            icon={<Trash2 className="w-4 h-4 text-red-600" />}
            disabled={!canDelete}
            onClick={() => handleDeleteAsset(record)}
          />
        </div>
      ),
    },
  ];

  // Handler for closing AssetModal and refetching assets
  const handleAssetModalClose = () => {
    setIsModalOpen(false);
    fetchAssets();
  };

  // Handler for closing EditAssetModal and refetching assets
  const handleEditAssetModalClose = () => {
    setIsEditModalOpen(false);
    fetchAssets();
  };

  return (
    <div className="px-4 pt-2 pb-4 flex flex-col gap-5 bg-white rounded-md ">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold mb-4">Assets</h2>
        <div className="flex justify-between items-center gap-2">
          <Button icon={<LeftOutlined />}>Back</Button>
          <Button icon={<BulbOutlined />}>Page Tip</Button>
        </div>
      </div>
      <div className="flex items-center justify-between gap-4 items-center mb-6">
        <Input.Search
          placeholder="Search assets..."
          style={{ width: 300 }}
          className="mr-4"
        />

        <Button
          type="primary"
          icon={<PlusOutlined />}
          className="bg-primary text-white"
          disabled={!canAdd}
          onClick={() => canAdd ? setIsModalOpen(true) : message.warning("You don't have permission to assign assets.")}
        >
          Assign Assets
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={canView ? assets : []}
        rowKey={record => record._id || record.serialNo || record.id}
        scroll={{ x: 'max-content' }}
        loading={loading}
        pagination={{
          total: canView ? assets.length : 0,
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} items`,
        }}
        className="bg-white rounded-lg border border-gray-200"
      />

      <AssetModal
        open={isModalOpen}
        onRequestClose={handleAssetModalClose}
      />
      <EditAssetModal
        open={isEditModalOpen}
        onRequestClose={handleEditAssetModalClose}
        assetToEdit={selectedAsset}
      />
      {!canView && (
        <div className="text-center text-gray-500 py-4">You don't have permission to view assets.</div>
      )}
    </div>
  );
};

export default Assets;
